/* eslint-disable indent */
'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import Image from 'next/image';
import { useLanguage } from '@/app/context/language-context';

// Mock testimonials data
const testimonials = [
  {
    id: 1,
    name: 'Fareed Ahmad',
    role: 'Property Investor',
    image:
      'https://images.x-space.ae/assets/images/logo-A.png?height=200&width=200',
    text: 'Ms. Maryam, a real estate agent from Dubai, contacted me and provided detailed and valuable information about the properties available for sale. She also shared some excellent presentations. Her convincing skills were impressive. I believe Ms. Maryam is a successful real estate agent and can be an outstanding asset to her organization, contributing significantly to its success and profitability.',
    rating: 5,
  },
  {
    id: 2,
    name: 'Hammad Ahmed',
    role: 'First-time Buyer',
    image:
      'https://images.x-space.ae/assets/images/logo-A.png?height=200&width=200',
    text: 'I contacted Pranav for off-plan property advice and ended up with a long-term investment partner. He walked me through every detail—developer reputation, payment plans, market cycles—and even flagged exclusive listings that weren’t publicly available. I’ve dealt with many agents in Dubai, but Pranav’s transparency and market insight truly stand out.',
    rating: 5,
  },
  {
    id: 3,
    name: 'Aldrin Edger',
    role: 'Luxury Villa Owner',
    image:
      'https://images.x-space.ae/assets/images/logo-A.png?height=200&width=200',
    text: 'I first reached out to Pranav with a few questions about off-plan projects, and it turned into one of the smartest investment decisions I’ve made. He explained the process clearly, highlighted opportunities I wouldn’t have found on my own, and gave me honest advice at every step. In a market full of sales pitches, his knowledge and straightforward approach made all the difference',
    rating: 5,
  },
  {
    id: 4,
    name: 'Asif Shahzad',
    role: 'International Client',
    image:
      'https://images.x-space.ae/assets/images/logo-A.png?height=200&width=200',
    text: 'Mr Ayan exceeded my expectations! His professionalism, market knowledge, and dedication was impressive. His expertise made the entire process smooth and stress-free. Highly recommend their services to anyone looking for a reliable and skilled real estate service',
    rating: 4,
  },
];

export default function TestimonialsSection() {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const sliderRef = useRef(null);
  const [autoPlay, setAutoPlay] = useState(true);
  const autoPlayRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const testimonialsLength = testimonials.length; // Extract length

  // Check if mobile on mount and window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const nextTestimonial = useCallback(() => {
    // if (isAnimating) return;
    // setIsAnimating(true);
    // setDirection(1);
    // setActiveIndex((prev) => (prev + 1) % testimonialsLength);
    // setTimeout(() => setIsAnimating(false), 500);

    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(1);
    const newIndex = (activeIndex + 1) % testimonials.length;
    setActiveIndex(newIndex);

    // Update scroll position for mobile
    if (sliderRef.current && isMobile) {
      const cardWidth = sliderRef.current.scrollWidth / testimonials.length;
      sliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  }, [activeIndex, isAnimating, isMobile]); // Now ESLint won't warn

  const prevTestimonial = () => {
    // if (isAnimating) return;
    // setIsAnimating(true);
    // setDirection(-1);
    // setActiveIndex(
    //   (prev) => (prev - 1 + testimonials.length) % testimonials.length
    // );
    // setTimeout(() => setIsAnimating(false), 500);

    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(-1);
    const newIndex =
      (activeIndex - 1 + testimonials.length) % testimonials.length;
    setActiveIndex(newIndex);

    // Update scroll position for mobile
    if (sliderRef.current && isMobile) {
      const cardWidth = sliderRef.current.scrollWidth / testimonials.length;
      sliderRef.current.scrollTo({
        left: newIndex * cardWidth,
        behavior: 'smooth',
      });
    }

    setTimeout(() => setIsAnimating(false), 500);
  };

  useEffect(() => {
    if (autoPlay) {
      autoPlayRef.current = setInterval(() => {
        nextTestimonial();
      }, 5000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [activeIndex, autoPlay, nextTestimonial]); // Now nextTestimonial is stable

  // Pause autoplay on hover
  const handleMouseEnter = () => {
    setAutoPlay(false);
  };

  const handleMouseLeave = () => {
    setAutoPlay(true);
  };

  // const nextTestimonial = () => {
  //   if (isAnimating) return;
  //   setIsAnimating(true);
  //   setDirection(1);
  //   setActiveIndex((prev) => (prev + 1) % testimonials.length);
  //   setTimeout(() => setIsAnimating(false), 500);
  // };

  // Mouse drag handlers for slider (mobile only)
  const handleMouseDown = (e) => {
    if (!sliderRef.current || !isMobile) return;
    setIsDragging(true);
    setStartX(e.pageX - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    sliderRef.current.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current || !isMobile) return;
    e.preventDefault();
    const x = e.pageX - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    sliderRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    if (!isMobile) return;
    setIsDragging(false);
    if (sliderRef.current) {
      sliderRef.current.style.cursor = 'grab';

      // Calculate which testimonial is most visible and set activeIndex accordingly
      if (sliderRef.current.scrollLeft > 0) {
        const cardWidth = sliderRef.current.offsetWidth;
        const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
        setActiveIndex(Math.min(newIndex, testimonials.length - 1));
      }
    }
  };

  const handleScroll = () => {
    if (!isMobile || !sliderRef.current) return;
    const cardWidth = sliderRef.current.offsetWidth;
    const newIndex = Math.round(sliderRef.current.scrollLeft / cardWidth);
    if (newIndex !== activeIndex) {
      setActiveIndex(Math.min(newIndex, testimonials.length - 1));
    }
  };

  // Render stars based on rating
  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, i) => (
      <svg
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
        fill='currentColor'
        viewBox='0 0 20 20'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path d='M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z' />
      </svg>
    ));
  };

  // Variants for the expo slider animation
  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction > 0 ? -30 : 30,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.5 },
        scale: { duration: 0.5 },
        rotateY: { duration: 0.5 },
      },
    },
    exit: (direction) => ({
      x: direction > 0 ? -1000 : 1000,
      opacity: 0,
      scale: 0.8,
      rotateY: direction > 0 ? 30 : -30,
      transition: {
        x: { type: 'spring', stiffness: 300, damping: 30 },
        opacity: { duration: 0.5 },
        scale: { duration: 0.5 },
        rotateY: { duration: 0.5 },
      },
    }),
  };

  return (
    <section className='py-6 px-4 bg-gray-50 dark:bg-gray-800 overflow-hidden'>
      <div className='max-w-7xl mx-auto'>
        <div className='text-center'>
          <h2 className='text-3xl font-bold text-gray-900 dark:text-white mb-4'>
            {t('clientTestimonials')}
          </h2>
          <p className='text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
            {t('clientTestimonials')}
          </p>
        </div>

        <div
          className={`relative perspective-1000 ${
            isMobile ? 'cursor-grab active:cursor-grabbing' : ''
          }`}
          ref={sliderRef}
          onMouseEnter={handleMouseEnter}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onScroll={handleScroll}
          style={{
            cursor:
              isDragging && isMobile
                ? 'grabbing'
                : isMobile
                  ? 'grab'
                  : 'default',
          }}
        >
          {isMobile ? (
            // Mobile view - horizontal scrolling
            <div
              className='flex overflow-x-auto snap-x snap-mandatory hide-scrollbar'
              style={{ scrollBehavior: 'smooth' }}
            >
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className='min-w-full snap-center p-4'
                >
                  <div className='bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 max-w-4xl mx-auto relative z-10 w-full'>
                    <div className='absolute top-8 left-8 text-primarycolor dark:text-secondarycolor opacity-20'>
                      <Quote size={60} />
                    </div>

                    <div className='flex flex-col md:flex-row items-center md:items-start gap-8'>
                      <div className='relative w-24 h-24 rounded-full overflow-hidden flex-shrink-0 z-10'>
                        <Image
                          src={testimonial.image || '/placeholder.svg'}
                          alt={testimonial.name}
                          fill
                          className='object-cover'
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                          unoptimized
                        />
                      </div>

                      <div className='z-10'>
                        <p className='text-gray-600 dark:text-gray-400 mb-4'>
                          "{testimonial.text}"
                        </p>

                        <div className='flex mb-2'>
                          {renderStars(testimonial.rating)}
                        </div>

                        <h4 className='text-xl font-bold text-gray-900 dark:text-white'>
                          {testimonial.name}
                        </h4>
                        {/* <p className='text-primarycolor dark:text-secondarycolor'>
                          {testimonial.role}
                        </p> */}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop view - controlled by navigation with animations
            <div className='relative h-[550px] md:h-[500px] lg:h-[300px] overflow-hidden'>
              <AnimatePresence initial={false} custom={direction} mode='wait'>
                <motion.div
                  key={activeIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial='enter'
                  animate='center'
                  exit='exit'
                  className='absolute inset-0 flex items-center justify-center'
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <div className='bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 max-w-4xl mx-auto relative z-10 w-full'>
                    <div className='absolute top-8 left-8 text-primarycolor dark:text-secondarycolor opacity-20'>
                      <Quote size={60} />
                    </div>

                    <div className='flex flex-col md:flex-row items-center md:items-start gap-8'>
                      <div className='relative w-24 h-24 rounded-full overflow-hidden flex-shrink-0 z-10'>
                        <Image
                          src={
                            testimonials[activeIndex].image ||
                            '/placeholder.svg'
                          }
                          alt={testimonials[activeIndex].name}
                          fill
                          className='object-cover'
                          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                          unoptimized
                        />
                      </div>

                      <div className='flex-1 z-10'>
                        <p className='text-lg text-gray-700 dark:text-gray-300 mb-6 italic relative'>
                          "{testimonials[activeIndex].text}"
                        </p>

                        <div className='flex mb-2'>
                          {renderStars(testimonials[activeIndex].rating)}
                        </div>

                        <h4 className='text-xl font-bold text-gray-900 dark:text-white'>
                          {testimonials[activeIndex].name}
                        </h4>
                        {/* <p className='text-primarycolor dark:text-secondarycolor'>
                          {testimonials[activeIndex].role}
                        </p> */}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* Navigation buttons */}
          <div className='absolute left-4 top-1/2 -translate-y-1/2 z-20 hidden md:block'>
            <motion.button
              onClick={prevTestimonial}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1, x: 5 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Previous testimonial'
            >
              <ChevronLeft className='w-5 h-5' />
            </motion.button>
          </div>

          <div className='absolute right-4 top-1/2 -translate-y-1/2 z-20 hidden md:block'>
            <motion.button
              onClick={nextTestimonial}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1, x: -5 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Next testimonial'
            >
              <ChevronRight className='w-5 h-5' />
            </motion.button>
          </div>

          {/* <div className='absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 flex space-x-4 md:hidden'>
            <motion.button
              onClick={prevTestimonial}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Previous testimonial'
            >
              <ChevronLeft className='w-5 h-5' />
            </motion.button>

            <motion.button
              onClick={nextTestimonial}
              className='p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-primarycolor hover:text-white transition-colors shadow-md'
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label='Next testimonial'
            >
              <ChevronRight className='w-5 h-5' />
            </motion.button>
          </div> */}

          {/* Mobile scroll indicator - Only visible on mobile */}
          <div
            className={`mb-4 mt-6 text-center text-sm text-gray-500 dark:text-gray-400 ${
              isMobile ? 'block' : 'hidden'
            }`}
          >
            <span className='inline-flex items-center'>
              <ChevronLeft className='w-4 h-4 mr-1' />
              Swipe to explore
              <ChevronRight className='w-4 h-4 ml-1' />
            </span>
          </div>

          {/* Dots navigation */}
          <div
            className={`flex justify-center mt-8 space-x-2 ${
              isMobile ? 'hidden' : 'block'
            }`}
          >
            {testimonials.map((_, index) => (
              // <button
              //   key={index}
              //   onClick={() => {
              //     setDirection(index > activeIndex ? 1 : -1);
              //     setActiveIndex(index);
              //   }}
              //   className={`w-3 h-3 rounded-full transition-colors ${
              //     activeIndex === index
              //       ? 'bg-primarycolor'
              //       : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
              //   }`}
              //   aria-label={`Go to testimonial ${index + 1}`}
              // />
              <button
                key={index}
                onClick={() => {
                  setDirection(index > activeIndex ? 1 : -1);
                  setActiveIndex(index);
                }}
                className={
                  'relative flex items-center justify-center min-w-[48px] min-h-[48px]'
                }
                aria-label={`Go to testimonial ${index + 1}`}
              >
                <span
                  className={`w-3 h-3 rounded-full transition-colors 
                                ${
                                  activeIndex === index
                                    ? 'bg-primarycolor'
                                    : 'bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600'
                                }
                              `}
                />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
